/**
 * The chat panel's conversation list: one page at a time, with a way down.
 *
 * `GET /api/sessions` is paginated, so the panel holds an accumulating list and
 * a cursor. Extracted from BirdChatPanel because the three failure modes here
 * are subtle and easier to see together: a refresh must not discard pages the
 * user paged in, a failed page must not be mistaken for "no more pages", and a
 * conversation that moves between requests must not appear twice.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { listAgentSessionPage } from '../../../services/agentChat.service';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import type { AgentSessionSummary } from '../../../services/agentChat.service';

type AgentSession = AgentSessionSummary;

/**
 * Newest first, one entry per conversation.
 *
 * Both properties are load-bearing once pages can be appended: the server
 * orders by `update_time DESC`, so a conversation that receives a reply
 * between two page requests moves up and can be returned on both — appending
 * blindly would show it twice. De-duplicating on the way in is cheaper than
 * reasoning about when that can happen.
 */
function mergeSessions(
  existing: readonly AgentSession[],
  incoming: readonly AgentSession[],
): AgentSession[] {
  const byId = new Map(existing.map((s) => [s.session_id, s]));
  for (const s of incoming) byId.set(s.session_id, s);
  return [...byId.values()].sort((a, b) => b.last_update_time - a.last_update_time);
}

export interface SessionList {
  readonly sessions: AgentSession[];
  readonly totalSessions: number;
  readonly isLoadingSessions: boolean;
  readonly isLoadingMoreSessions: boolean;
  readonly hasMoreSessions: boolean;
  readonly loadSessions: () => Promise<void>;
  readonly loadMoreSessions: () => Promise<void>;
  readonly renameSession: (sessionId: string, title: string) => void;
  readonly forgetSession: (sessionId: string) => void;
}

export function useSessionList(): SessionList {
  const workspaceId = useWorkspaceId();

  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingMoreSessions, setIsLoadingMoreSessions] = useState(false);
  /** Where the next page starts, counted in rows the server actually returned. */
  const nextOffsetRef = useRef(0);

  // Refresh page one, KEEPING anything already paged in.
  //
  // This runs whenever the menu opens and after every answer settles, so a
  // plain reset silently threw away every "Load more" the user had clicked —
  // the feature had to be re-earned on each menu open, which is most of the
  // reason it exists.
  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const page = await listAgentSessionPage(0);
      if (page.failed) return;
      // `max`, not assignment: a refresh re-reads page one, but the user may
      // already have paged well past it, and resetting the cursor here would
      // make the next "Load more" re-fetch ground already covered.
      nextOffsetRef.current = Math.max(nextOffsetRef.current, page.sessions.length);
      setSessions((prev) => mergeSessions(prev, page.sessions));
      setTotalSessions(page.total);
    } catch {
      // Keep whatever is on screen; an empty list here is indistinguishable
      // from "you have no conversations".
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  // Fetch the next page and append it.
  //
  // The list used to be unbounded, so every conversation was always present.
  // Paginating the endpoint without this made older conversations unreachable
  // from the UI entirely — silently, with no affordance and no error.
  const loadMoreSessions = useCallback(async () => {
    setIsLoadingMoreSessions(true);
    try {
      const page = await listAgentSessionPage(nextOffsetRef.current);
      // A failed page must NOT write `total`. It returns 0, which reads as
      // "nothing more to load" and removes the Load more row entirely — one
      // transient 5xx and older conversations are unreachable again, silently,
      // which is the exact state this row exists to fix.
      if (page.failed) return;
      // Advance by what the SERVER returned, not by the deduplicated list
      // length: a conversation that moves between requests is absorbed by the
      // merge, so list length under-counts the rows already emitted and the
      // next offset would walk back over ground already covered.
      nextOffsetRef.current += page.sessions.length;
      setSessions((prev) => mergeSessions(prev, page.sessions));
      setTotalSessions(page.total);
    } catch {
      // Leave what is already loaded on screen — a failed "load more" should
      // cost the user the extra page, not the list they were looking at.
    } finally {
      setIsLoadingMoreSessions(false);
    }
  }, []);
  // Switching workspace must EMPTY the list, not merge into it.
  //
  // This is plain React state, and `switchWorkspace` does not remount the tree
  // — it clears TanStack Query, which this is not. Since `mergeSessions` is a
  // union that never removes, the previous workspace's conversations simply
  // stayed on screen. Conversation titles are user-authored and can name a
  // person, a client or an unreleased result, so that is a cross-workspace
  // disclosure, not a stale-cache annoyance.
  useEffect(() => {
    setSessions([]);
    setTotalSessions(0);
    nextOffsetRef.current = 0;
  }, [workspaceId]);

  const hasMoreSessions = sessions.length < totalSessions;

  /** Apply a new title locally, so the row updates without a refetch. */
  const renameSession = useCallback((sessionId: string, title: string) => {
    setSessions((prev) => prev.map((s) => (s.session_id === sessionId ? { ...s, title } : s)));
  }, []);

  /** Drop a deleted conversation, keeping the paging counters honest. */
  const forgetSession = useCallback((sessionId: string) => {
    setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
    // Or `sessions.length < totalSessions` stays true against a list that is
    // now complete, and a "Load more" row appears that fetches nothing.
    setTotalSessions((t) => Math.max(0, t - 1));
    nextOffsetRef.current = Math.max(0, nextOffsetRef.current - 1);
  }, []);

  return {
    sessions,
    totalSessions,
    isLoadingSessions,
    isLoadingMoreSessions,
    hasMoreSessions,
    loadSessions,
    loadMoreSessions,
    renameSession,
    forgetSession,
  };
}
